import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/common';
import { Button, Spinner } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { AuthContext } from '@/contexts';
import { VideoThemeContext } from '@/contexts/VideoThemeProvider';
import { PERMISSION_TYPE } from '@/enums';
import { getVideoThemeClipColumns } from '@/helpers/VideoThemeClipsHelper';
import { CreateEditClip } from '@/pages/VideoTheme/Clips/CreateEdit';
import { PlusIcons } from '@/resources';
import {
  useDeleteVideoThemeClipMutation,
  useGetClipsByVideoThemeIdQuery
} from '@/services/videoThemeClips.service';
import { AllowedPermissions, TableData } from '@/types';
import { VideoThemeClip } from '@/types/VideoThemeTypes';

type VideoThemeClipsProps = {
  id: string;
};

export const VideoThemeClips = ({ id }: VideoThemeClipsProps) => {
  const [t] = useTranslation('common');
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);
  const { refetch: refetchTheme } = useContext(VideoThemeContext);
  const {
    data: videoThemeClips,
    isLoading: isLoadingClips,
    refetch
  } = useGetClipsByVideoThemeIdQuery(Number(id));
  const [selectedClip, setSelectedClip] = useState<VideoThemeClip | undefined>();
  const [isAddNewClipModalOpen, setIsAddNewClipModalOpen] = useState<boolean>(false);
  const [isEditClipModalOpen, setIsEditClipModalOpen] = useState<boolean>(false);

  const allowedPermissions: AllowedPermissions = {
    createPermission: PERMISSION_TYPE.CREATE_VIDEO_THEME,
    editPermission: PERMISSION_TYPE.UPDATE_VIDEO_THEME,
    deletePermission: PERMISSION_TYPE.DELETE_VIDEO_THEME
  };

  const { permissions } = useContext(AuthContext);
  const editVideoThemeAccess = permissions.includes(PERMISSION_TYPE.UPDATE_VIDEO_THEME);
  const createVideoThemeAccess = permissions.includes(PERMISSION_TYPE.CREATE_VIDEO_THEME);

  const handleEdit = (themeData: VideoThemeClip) => {
    setIsEditClipModalOpen(true);
    setSelectedClip(themeData);
  };

  const handleViewDetails = (themeData: VideoThemeClip) => {
    if (editVideoThemeAccess) {
      setIsEditClipModalOpen(true);
      setSelectedClip(themeData);
    }
  };

  const handleDeleteClip = (row: VideoThemeClip) => {
    setIsOpenDeleteModal(true);
    setSelectedClip(row);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedClip(undefined);
    refetchTheme();
  };

  const { mutate: deleteClip } = useDeleteVideoThemeClipMutation(queryClient, {
    onSuccess: () => {
      setIsOpenDeleteModal(false);
      refetch();
      refetchTheme();
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const formatData = (): TableData[] => {
    return videoThemeClips?.data.map((item: VideoThemeClip) => ({
      id: item.id,
      type: item.type,
      order: item.order,
      assetName: item.asset?.assetName,
      asset: item.asset,
      enabled: true,
      duration: item.duration
    })) as TableData[];
  };

  return (
    <div className='mt-5 w-full overflow-auto md:mt-7 md:px-10'>
      {isLoadingClips ? (
        <Spinner />
      ) : (
        <div>
          {videoThemeClips && (
            <Table
              data={formatData()}
              columns={getVideoThemeClipColumns()}
              permissions={allowedPermissions}
              handleEdit={(item) => handleEdit(item as VideoThemeClip)}
              handleDelete={(item) => handleDeleteClip(item as VideoThemeClip)}
              handleViewDetails={(item) => handleViewDetails(item as VideoThemeClip)}
              fetchNextPage={() => {
                throw new Error('Function not implemented.');
              }}
              hasNextPage={undefined}
              isFetchingNextPage={false}
              numberOfAppliedFilters={0}
              hiddenColumnsMobile={['enabled']}
              mobileColumnOffset={3}
            />
          )}
          {selectedClip && (
            <ConfirmationModal
              isOpen={isOpenDeleteModal}
              message={t('deleteMessage', {
                item: `${selectedClip.type} ${selectedClip.order}`
              })}
              onClose={handleCloseDeleteModal}
              onConfirm={() => {
                selectedClip && selectedClip.id && deleteClip(selectedClip.id);
              }}
            />
          )}
          {createVideoThemeAccess && (
            <div className='flex w-full flex-col justify-between'>
              <Button
                className='mx-[35px] mb-4 mt-5'
                variant={ButtonVariant.QUINARY}
                handleOnClick={() => {
                  setIsAddNewClipModalOpen(true);
                }}>
                <PlusIcons className='size-3.5 md:mr-3' fill='var(--color-primary-blue)' />
                {t('addNewClip')}
              </Button>
            </div>
          )}
          {isAddNewClipModalOpen && (
            <CreateEditClip
              isOpen={isAddNewClipModalOpen}
              isCreate={true}
              videoThemeId={Number(id)}
              onClose={() => {
                setIsAddNewClipModalOpen(false);
                refetch();
                refetchTheme();
              }}
            />
          )}
          {isEditClipModalOpen && (
            <CreateEditClip
              isOpen={isEditClipModalOpen}
              isCreate={false}
              clip={selectedClip}
              onClose={() => {
                setIsEditClipModalOpen(false);
                setSelectedClip(undefined);
                refetch();
                refetchTheme();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
